/* ============================================================
   Laley PDP Clean v4 — sticky
   Modulær PDP-runtime. Den gamle v3-monolitten er kun historisk arkiv.
   Registrerer metoder på LaleyPDP.prototype via PDP.registerModule.
   Krever pdp-clean-v4-core.js lastet først (defer-rekkefølge i product-clean-v3-1.liquid).
   ============================================================ */
(() => {
  const PDP = window.LaleyPDPCleanV4;
  if (!PDP || typeof PDP.registerModule !== 'function') {
    console.error('[PDP] Kjerne mangler — hopper over modul: sticky');
    return;
  }
  const { cssEscape } = PDP.utils;

  PDP.registerModule('sticky', {
    refreshStickyMetrics() {
      const cssHeight = parseFloat(
        window.getComputedStyle(this.section).getPropertyValue('--pdp-sticky-mobile-height')
      );
      const measuredHeight = this.sticky && !this.sticky.hidden
        ? Math.ceil(this.sticky.getBoundingClientRect().height)
        : 0;
      const fallbackHeight = Number.isFinite(cssHeight) && cssHeight > 0 ? cssHeight : 58;
      const rawHeight = Number.isFinite(measuredHeight) && measuredHeight > 0
        ? measuredHeight
        : fallbackHeight;
      const isPhone = window.matchMedia('(max-width: 749px)').matches;
      const isTablet = window.matchMedia('(max-width: 989px)').matches;
      const minHeight = isPhone ? 58 : 58;
      const maxHeight = isPhone ? 74 : (isTablet ? 82 : 90);
      this.mobileStickyHeight = Math.max(minHeight, Math.min(maxHeight, rawHeight));
      this.section.style.setProperty('--pdp-sticky-mobile-height', `${this.mobileStickyHeight}px`);
    },

    updateStickyContent(variant) {
      if (this.stickyPrice && variant) this.stickyPrice.textContent = this.money(variant.price);
      if (this.stickyButton) {
        // Sticky-knappen speiler hoved-ATC: velg størrelse først, kjøp når varianten er valgt.
        if (!this.hasRequiredSizeSelection()) {
          this.stickyButton.disabled = false;
          this.stickyButton.dataset.mode = 'pick';
          this.stickyButton.textContent = 'Velg størrelse';
        } else if (!variant) {
          this.stickyButton.disabled = true;
          this.stickyButton.dataset.mode = 'unavailable';
          this.stickyButton.textContent = 'Velg alternativer';
        } else if (!variant.available) {
          // Utsolgt valgt størrelse → speil hoved-ATC sin BIS-state (klikkbar varsling).
          // Klikk proxyer til form.requestSubmit(atc) som kjører eksisterende BIS-flow.
          this.stickyButton.disabled = false;
          this.stickyButton.dataset.mode = 'bis';
          this.stickyButton.textContent = 'Varsle';
        } else {
          this.stickyButton.disabled = false;
          this.stickyButton.dataset.mode = 'add';
          this.stickyButton.textContent = 'Kjøp';
        }
      }
      // Sticky-thumben er packshoten (siste bilde, server-rendret) og er
      // variant-uavhengig — ingen swap ved variantbytte.
      this.updateStickyVisibility();
    },

    bindStickyPicker() {
      // LSF-stil: inline size-picker i sticky-bar. Native <select> for kompakthet,
      // mirrorer hoved-pickerens valg + opdaterer ved variant-change.
      const slot = this.section.querySelector('[data-pdp-sticky-picker]');
      if (!slot) return;
      // Finn første ikke-color picker (typisk størrelse). Bundle-set bruker
      // delt picker — vi mirrorer den om finnes, ellers første size-group.
      const sizeGroup = this.optionGroups.find((g) => {
        const legend = (g.querySelector('legend')?.textContent || '').toLowerCase();
        return !/farge|color|colour/.test(legend);
      });
      if (!sizeGroup) return;
      const inputs = Array.from(sizeGroup.querySelectorAll('[data-pdp-option-input]'));
      if (inputs.length < 2) return;

      const select = document.createElement('select');
      select.className = 'pdp-clean__sticky-select';
      select.setAttribute('aria-label', 'Velg størrelse');
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Størrelse'; // knappen sier «Velg størrelse» — ikke dupliser
      placeholder.disabled = true;
      placeholder.selected = true;
      select.appendChild(placeholder);
      inputs.forEach((input) => {
        const opt = document.createElement('option');
        opt.value = input.value;
        opt.textContent = input.value;
        opt.disabled = input.disabled;
        select.appendChild(opt);
      });
      slot.appendChild(select);
      slot.hidden = false;

      // Change i sticky-select →
      // 1) Hvis bundle-shared finnes: click matching pill (som synker til alle 3)
      // 2) Ellers: check radio i hoved-pickeren
      this.on(select, 'change', () => {
        const value = select.value;
        if (!value) return;
        const sharedPill = this.section.querySelector(`[data-pdp-bundle-shared]:not([hidden]) .pdp-clean__bundle-shared-pill[data-value="${cssEscape(value)}"]`);
        if (sharedPill) {
          sharedPill.click();
          return;
        }
        // For bundle uten shared (mix-mode aktiv): sync til ALLE size-grupper
        const allSizeGroups = this.optionGroups.filter((g) => {
          const legend = (g.querySelector('legend')?.textContent || '').toLowerCase();
          return !/farge|color|colour/.test(legend);
        });
        allSizeGroups.forEach((group) => {
          const radio = group.querySelector(`[data-pdp-option-input][value="${cssEscape(value)}"]`);
          if (radio && !radio.disabled) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
      // Når hoved-radio endres → speil i sticky-select
      inputs.forEach((input) => {
        this.on(input, 'change', () => {
          if (input.checked) select.value = input.value;
        });
      });
    },

    bindSticky() {
      if (!this.sticky || !this.stickyButton || !this.atc) return;
      this.bindStickyPicker();
      this.on(this.stickyButton, 'click', () => {
        if (!this.form || this.stickyButton.disabled) return;
        // I "pick"-mode: scroll til STØRRELSE-gruppen + highlight den. IKKE add-to-cart.
        // Viktig: optionGroups[0] er Farge (pickere rendres farge-først), så vi må
        // finne første ikke-farge-gruppe (størrelse), ikke bare ta [0].
        if (this.stickyButton.dataset.mode === 'pick') {
          // Stabil target: Liquid markerer størrelse-fieldset med data-pdp-size-group.
          // Fallback til legend-tekst kun hvis attributtet mangler (eldre markup).
          const sharedPicker = this.section.querySelector('[data-pdp-bundle-shared]:not([hidden])');
          const sizeGroup = this.section.querySelector('[data-pdp-size-group]')
            || this.optionGroups.find((g) => {
              const legend = (g.querySelector('legend')?.textContent || '').toLowerCase();
              return !/farge|color|colour/.test(legend);
            })
            || this.optionGroups[0];
          const target = sharedPicker || sizeGroup;
          if (target) {
            // Åpne ev. dropdown (kun hvis dropdown-modus er på)
            target.querySelectorAll('[data-pdp-dropdown]').forEach((d) => { d.open = true; });
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Fokuser dropdown-trigger (dropdown-modus) eller første ledige størrelse (knapp-modus)
            const focusEl = target.querySelector('.pdp-clean__bundle-shared-pill:not(:disabled), .pdp-clean__dropdown-trigger, [data-pdp-option-input]:not(:disabled)');
            if (focusEl && typeof focusEl.focus === 'function') {
              try { focusEl.focus({ preventScroll: true }); } catch (e) { focusEl.focus(); }
            }
            // Visuell puls så brukeren ser HVOR de skal velge
            target.classList.add('pdp-clean__picker--flash');
            window.setTimeout(() => target.classList.remove('pdp-clean__picker--flash'), 1300);
          }
          return;
        }
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.atc);
        else this.atc.click();
      });

      const request = () => {
        if (this.stickyRaf) return;
        this.stickyRaf = window.requestAnimationFrame(() => {
          this.stickyRaf = null;
          this.updateStickyVisibility();
        });
      };
      const requestWithMetrics = () => {
        this.refreshStickyMetrics();
        request();
      };
      this.on(window, 'scroll', request, { passive: true });
      this.on(window, 'resize', requestWithMetrics, { passive: true });
      this.on(window, 'orientationchange', requestWithMetrics, { passive: true });
      this.updateStickyVisibility();
    },

    updateStickyVisibility() {
      if (!this.sticky || !this.atc) return;
      // Show the sticky CTA whenever the main CTA is outside the active buying
      // area. On mobile this covers both states: before the user reaches the
      // size picker, and after the main CTA has scrolled past.
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const atcRect = this.atc.getBoundingClientRect();
      let galleryVisible = false;
      if (isMobile && this.galleryWrap) {
        const galleryRect = this.galleryWrap.getBoundingClientRect();
        galleryVisible = galleryRect.bottom > 0 && galleryRect.top < window.innerHeight;
      }
      const stickyHeight = isMobile
        ? this.mobileStickyHeight
        : (this.sticky.hidden ? 64 : this.sticky.getBoundingClientRect().height);
      const footer = document.querySelector('.laley-footer');
      const footerRect = footer ? footer.getBoundingClientRect() : null;
      const footerVisible = !!footerRect
        && footerRect.bottom > 0
        && footerRect.top < (window.innerHeight + Math.min(stickyHeight + 16, 92));
      const activeBottom = window.innerHeight - stickyHeight - 12;
      const show = isMobile
        ? !footerVisible && (atcRect.top > activeBottom || atcRect.bottom < 0)
        : !footerVisible && atcRect.bottom < 0;
      this.sticky.hidden = !show;
      this.section.classList.toggle('pdp-clean--sticky-visible', show);
      this.section.classList.toggle('pdp-clean--gallery-visible', galleryVisible);
      this.section.classList.toggle('pdp-clean--footer-visible', footerVisible);
      if (isMobile && show) this.refreshStickyMetrics();
    },
  });
})();
